import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Image,
  ScrollView,
} from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import ChatIframe from "../../../../../Components/stream-setup-components/chat-ui-frame";
import { AppFontStyle } from "../../../../../styles/AppFontStyle";
import { AppColors } from "../../../../../utility/AppColors";
import { AppImages } from "../../../../../utility/AppImages";
import { STREAM_PLATFORMS } from "../../../../../utility/LiveStreamSocket/LiveStreamConfig";

interface ChatMessage {
  id: string;
  username: string;
  message: string;
  avatar: string;
  timestamp: number;
}

const ChatRow = React.memo(({ item }: { item: ChatMessage }) => {
  const styles = useStyles();
  return (
    <View style={styles.chatRow}>
      <Image
        source={item.avatar ? { uri: item.avatar } : AppImages.Ellipse1}
        style={{
          width: 27,
          height: 27,
          borderRadius: 100,
        }}
      />
      <View style={{ flexDirection: "row", gap: 7, alignItems: "center" }}>
        <Text
          style={[
            AppFontStyle.MEDIUM_ANYBODY_12,
            { color: AppColors.gray_BBB },
          ]}
        >
          {item.username}
        </Text>
        <Text style={AppFontStyle.MEDIUM_ANYBODY_12}>{item.message}</Text>
      </View>
    </View>
  );
});

ChatRow.displayName = "ChatRow";

export interface LiveEventChatProps {
  isExpandChat: boolean;
  setIsExpandChat: (value: boolean) => void;
  isChatLocked?: boolean;
  connectedPlatforms: {
    twitch?: boolean;
    youtube?: boolean;
    kick?: boolean;
  };
  messages: ChatMessage[];
  onSendMessage: (text: string) => void;
  twitchUsername?: string;
  youtubeVideoId?: string;
}

const LiveEventChat: React.FC<LiveEventChatProps> = ({
  isExpandChat,
  setIsExpandChat,
  isChatLocked = false,
  connectedPlatforms,
  messages,
  onSendMessage,
  twitchUsername,
  youtubeVideoId,
}) => {
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const scrollViewRef = useRef<ScrollView>(null);

  // Tabs management
  const tabs = useMemo(() => {
    const availableTabs = ["KO Chat"];
    if (connectedPlatforms.twitch)
      availableTabs.push(STREAM_PLATFORMS.TWITCH_CAPITALIZED);
    if (connectedPlatforms.youtube)
      availableTabs.push(STREAM_PLATFORMS.YOUTUBE_CAPITALIZED);
    return availableTabs;
  }, [connectedPlatforms]);

  const [selectedTab, setSelectedTab] = useState("KO Chat");
  const [newMessage, setNewMessage] = useState("");
  // const [chatData, setChatData] = useState<ChatMessage[]>([]); // Using props.messages now
  const lastScrollTime = useRef(0);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // Update selected tab if it becomes unavailable
  useEffect(() => {
    if (!tabs.includes(selectedTab)) {
      setSelectedTab("KO Chat");
    }
  }, [tabs, selectedTab]);

  // Memoized filtered chat data - For KO Chat we use props.messages
  const filteredChatData = useMemo(() => {
    if (selectedTab !== "KO Chat") return [];
    return messages;
  }, [messages, selectedTab]);

  // Handle scroll position detection
  const handleScroll = useCallback(
    (event: any) => {
      const { contentOffset, contentSize, layoutMeasurement } =
        event.nativeEvent;
      const paddingToBottom = 20;
      const isBottom =
        layoutMeasurement.height + contentOffset.y >=
        contentSize.height - paddingToBottom;

      setIsAtBottom(isBottom);
      setShowScrollToBottom(!isBottom && filteredChatData.length > 0);
    },
    [filteredChatData.length]
  );

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    if (!scrollViewRef.current || filteredChatData.length === 0) return;
    try {
      scrollViewRef.current.scrollToEnd({ animated: true });
      setShowScrollToBottom(false);
    } catch (e) {
      console.error("Error scrolling to bottom:", e);
    }
  }, [filteredChatData.length]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    const now = Date.now();
    if (
      selectedTab === "KO Chat" &&
      filteredChatData.length > 0 &&
      isAtBottom &&
      now - lastScrollTime.current > 100
    ) {
      lastScrollTime.current = now;
      const timer = setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [filteredChatData.length, selectedTab, isAtBottom]);

  const handleSendMessage = useCallback(() => {
    if (newMessage.trim()) {
      onSendMessage(newMessage);
      setNewMessage("");
    }
  }, [newMessage, onSendMessage]);

  const handleTextChange = useCallback(
    (text: string) => {
      if (text.endsWith("\n")) {
        handleSendMessage();
      } else {
        setNewMessage(text);
      }
    },
    [handleSendMessage]
  );

  const isMobile =
    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  const isPlatformChat =
    selectedTab === STREAM_PLATFORMS.YOUTUBE_CAPITALIZED ||
    selectedTab === STREAM_PLATFORMS.TWITCH_CAPITALIZED;

  const canSendMessage = !isChatLocked && !isPlatformChat;

  return (
    <View style={styles.container}>
      {/* Header with Stats Removed */}
      <View style={{ paddingVertical: 7, paddingHorizontal: 10 }}>
        <Text style={[AppFontStyle.BOLD_ANYBODY_12, { marginBottom: 6 }]}>
          Live Event Chat
        </Text>
        <View style={styles.tabsContainer}>
          {tabs.map((tab, index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.tabButton,
                selectedTab === tab && styles.selectedTab,
              ]}
              onPress={() => setSelectedTab(tab)}
            >
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_12,
                  { textAlign: "center" },
                ]}
              >
                {tab}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Chat Container */}
      <View style={[styles.chatContainer, isPlatformChat && { height: 420 }]}>
        {/* Scroll to Bottom Button */}
        {showScrollToBottom && !isChatLocked && (
          <TouchableOpacity
            onPress={scrollToBottom}
            style={styles.scrollToBottomBtn}
          >
            <Text style={{ color: "white", fontSize: 20, fontWeight: "bold" }}>
              ↓
            </Text>
          </TouchableOpacity>
        )}

        {isChatLocked ? (
          <View style={styles.chatLockedContainer}>
            <Image
              source={AppImages.icLock}
              style={styles.lockIcon}
              resizeMode="contain"
            />
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_16,
                { color: AppColors.white, marginTop: 15 },
              ]}
            >
              Chat Lock
            </Text>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_12,
                {
                  color: AppColors.gray_BBB,
                  marginTop: 10,
                  textAlign: "center",
                  paddingHorizontal: 20,
                },
              ]}
            >
              KO Chat will appear here when the challenge is accepted
            </Text>
          </View>
        ) : selectedTab === "KO Chat" ? (
          <ScrollView
            ref={scrollViewRef}
            style={[
              isMobile && {
                height: isExpandChat ? 365 : 114,
              },
            ]}
            contentContainerStyle={{
              paddingBottom: 10,
              flexGrow: 1,
              justifyContent: "flex-end",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            {filteredChatData.length === 0 && (
              <View style={styles.chatPlaceholder}>
                <Text
                  style={[
                    AppFontStyle.MEDIUM_ANYBODY_12,
                    { color: AppColors.gray_BBB },
                  ]}
                >
                  No messages yet
                </Text>
              </View>
            )}
            {filteredChatData.map((item) => (
              <ChatRow key={item.id} item={item} />
            ))}
          </ScrollView>
        ) : (
          <ChatIframe
            platform={selectedTab}
            liveChatUrl={
              selectedTab === STREAM_PLATFORMS.TWITCH_CAPITALIZED &&
              twitchUsername
                ? `https://www.twitch.tv/embed/${twitchUsername}/chat?parent=${
                    typeof window !== "undefined"
                      ? window.location.hostname
                      : "localhost"
                  }`
                : selectedTab === STREAM_PLATFORMS.YOUTUBE_CAPITALIZED &&
                  youtubeVideoId
                ? `https://www.youtube.com/live_chat?v=${youtubeVideoId}&embed_domain=${
                    typeof window !== "undefined"
                      ? window.location.hostname
                      : "localhost"
                  }`
                : null
            }
            isLive={true}
          />
        )}
      </View>

      {/* Input Container */}
      {!isPlatformChat && (
        <View
          style={[
            styles.inputContainer,
            !canSendMessage && styles.inputContainerDisabled,
          ]}
        >
          <TextInput
            style={[
              styles.textInput,
              !canSendMessage && styles.textInputDisabled,
            ]}
            placeholder="Send a message"
            placeholderTextColor={AppColors.grayColor_484848}
            value={newMessage}
            onChangeText={handleTextChange}
            onSubmitEditing={handleSendMessage}
            returnKeyType="send"
            blurOnSubmit={false}
            editable={canSendMessage}
            pointerEvents={canSendMessage ? "auto" : "none"}
          />
          <TouchableOpacity
            onPress={handleSendMessage}
            style={[styles.sendButton, !canSendMessage && { opacity: 0.5 }]}
            disabled={!canSendMessage || newMessage.trim() === ""}
          >
            <Image source={AppImages.icSendIcon} style={styles.sendIcon} />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    container: {
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: AppColors.purple_453B50,
      height: 480, // Consistent height
      display: "flex",
      flexDirection: "column",
    },
    tabsContainer: {
      flexDirection: "row",
      gap: 6,
    },
    tabButton: {
      padding: 8,
      borderRadius: 5,
      flex: 1,
      backgroundColor: AppColors.blackTrans_000000a8,
    },
    selectedTab: {
      backgroundColor: AppColors.sidebarBorder_374151,
    },
    chatContainer: {
      flex: 1, // Take remaining space
      paddingHorizontal: 12,
      backgroundColor: AppColors.sidebarBackground_111827,
      position: "relative",
    },
    chatRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 8,
    },
    avatar: {
      width: 20,
      height: 20,
      borderRadius: 19,
      marginRight: 5,
    },
    inputContainer: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 15,
      paddingHorizontal: 20,
      borderTopWidth: 1,
      borderTopColor: AppColors.sidebarBorder_374151, // Visual separation
    },
    inputContainerDisabled: {
      opacity: 0.5,
    },
    textInput: {
      flex: 1,
      color: AppColors.white,
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 17,
      backgroundColor: AppColors.black_18151b,
    },
    textInputDisabled: {
      backgroundColor: AppColors.gray_1E1E1E,
    },
    sendButton: {
      marginLeft: 10,
    },
    sendIcon: {
      width: 20,
      height: 20,
    },
    chatPlaceholder: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    chatLockedContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      paddingHorizontal: 20,
    },
    lockIcon: {
      width: 60,
      height: 60,
      tintColor: AppColors.white,
    },
    scrollToBottomBtn: {
      position: "absolute",
      bottom: 10,
      right: 10,
      backgroundColor: "rgba(220, 38, 38, 0.9)",
      borderRadius: 20,
      width: 40,
      height: 40,
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
  },
  {
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.SM]: {},
    [DEVICE_SIZES.XS]: {},
  }
);

export default LiveEventChat;
